import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp-bot Hot Alerts — leads who chose "Call Now"/Founding on the Aayush bot.
// Polls every 30s so the team is alerted instantly.
export default function HotAlertsView({ token, onCountChange }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const pollRef = useRef(null);
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/hot-leads?status=pending&limit=50`, { headers });
      const d = await res.json();
      if (d.success) { setAlerts(d.alerts || []); onCountChange && onCountChange(d.pending || 0); }
    } catch (e) { console.error('hot alerts', e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    pollRef.current = setInterval(fetchAlerts, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchAlerts]);

  const resolve = async (id) => {
    setResolvingId(id);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/hot-leads/${id}/resolve`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: 'Contacted from CRM' }),
      });
      const d = await res.json();
      if (d.success) { setAlerts(prev => prev.filter(a => a.id !== id)); toast.success('✓ संपर्क मार्क हुआ'); }
      else toast.error('अपडेट नहीं हो सका');
    } catch (e) { toast.error('अपडेट नहीं हो सका'); }
    finally { setResolvingId(null); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="crm-hot-alerts-view">
      <div className="px-4 py-3 bg-gradient-to-r from-red-50 to-orange-50 border-b flex items-center justify-between">
        <div>
          <p className="font-bold text-red-700 text-sm">🔥 Hot Leads — तुरंत follow-up करें</p>
          <p className="text-[11px] text-slate-500">WhatsApp bot पर जिन्होंने "अभी बात करें" या Founding चुना · हर 30s auto-refresh</p>
        </div>
        <button onClick={fetchAlerts} data-testid="crm-hot-alerts-refresh" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50">↻ Refresh</button>
      </div>
      {loading ? (
        <div className="py-12 text-center"><div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>
      ) : alerts.length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm" data-testid="crm-hot-alerts-empty">✅ अभी कोई pending hot lead नहीं</div>
      ) : (
        <div className="divide-y">
          {alerts.map((a) => (
            <div key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`crm-hot-alert-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{a.name || 'WhatsApp Lead'}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">{a.intent_label}</span>
                  {a.alert_count > 1 && <span className="text-xs text-slate-400">×{a.alert_count}</span>}
                </div>
                <p className="text-sm text-slate-600 mt-1">📱 {a.phone}{a.summary ? ` · ${a.summary}` : ''}</p>
                <p className="text-[11px] text-slate-400 mt-1">{a.created_at ? new Date(a.created_at).toLocaleString('en-IN') : ''}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`https://wa.me/${(a.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`crm-hot-alert-wa-${a.id}`} className="text-xs px-3 py-2 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600">💬 WhatsApp</a>
                <a href={`tel:${(a.phone || '').replace(/[^0-9+]/g, '')}`} data-testid={`crm-hot-alert-call-${a.id}`}
                  className="text-xs px-3 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600">📞 Call</a>
                <button onClick={() => resolve(a.id)} disabled={resolvingId === a.id} data-testid={`crm-hot-alert-resolve-${a.id}`}
                  className="text-xs px-3 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 disabled:opacity-50">
                  {resolvingId === a.id ? '...' : '✓ संपर्क किया'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
